import {Component, ViewEncapsulation} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { AlmanacPass, ApiGatewayService, ConstellationCode, CountryCode, HARDWARE_CAPABILITIES, HardwareCapability, SERVICE_TYPES, ServiceType } from 'src/app/services/api-gateway.service';
import { KeycloakUserService } from 'src/app/services/keycloak-user.service';
import { InfoMessageService, emitInfoMessage } from 'src/app/services/info-message.service';
import { Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment.production';

export interface GroupDayName {
  groupName: string;
}

export const LACUNA_LOCAL_STORAGE_PP_NAME : string = 'lacuna-dashboard-store-passprediction-inputs'

@Component({
    selector: 'tab-pass-prediction',
    templateUrl: './tab-pass-prediction.component.html',
    standalone: false
})
export class TabPassPredictionComponent {
  passDataFromApi : AlmanacPass[] = [];
  passDataWithGroupHeaders : (AlmanacPass|GroupDayName)[] = [];
  countryCodes : CountryCode[] = [];
  almanacVersions : string[] = [];
  serviceTypes : ServiceType[] = SERVICE_TYPES;
  hardwareCapabilities : HardwareCapability[] = HARDWARE_CAPABILITIES;
  todayDate : Date =  new Date();
  countryList?: Observable<CountryCode[]>;
  dateOptions : string[] = ['Today','Custom'];

  passPredictionForm = new FormGroup({
    dashboardVersion: new FormControl<string>(environment.version),
    dateOption: new FormControl<string|null>(this.dateOptions[0],[Validators.required]),
    date: new FormControl<Date>(this.todayDate,[Validators.required]),
    countryCode: new FormControl<string|null>(null,[Validators.required]),
    almanacVersion: new FormControl<string|null>(null,Validators.required),
    useLocalTime: new FormControl<boolean>(true, {nonNullable: true}),
    serviceType: new FormGroup({}),
    hardwareCapability: new FormGroup({}),
    location: new FormGroup({
      latitude: new FormControl<number|null>(null,[Validators.required, Validators.min(-90),Validators.max(90)]),
      longitude: new FormControl<number|null>(null,[Validators.required, Validators.min(-180),Validators.max(180)]),
    })
  });

  constructor(private infoMessageService: InfoMessageService,
              private gatewayApiService: ApiGatewayService,
              private keycloakUserService: KeycloakUserService){}

  async ngOnInit(){
    // Almanacs
    this.almanacVersions = this.keycloakUserService.getUserAllowedAlmanacs();

    // Initialize FormGroups for checkboxes BEFORE localStorage init
    this.serviceTypes.forEach(service =>
      (this.passPredictionForm.controls.serviceType as FormGroup).addControl(
        service.id,
        new FormControl<boolean>(false, {nonNullable: true})
      )
    );
    this.hardwareCapabilities.forEach(hw =>
      (this.passPredictionForm.controls.hardwareCapability as FormGroup).addControl(
        hw.id,
        new FormControl<boolean>(false, {nonNullable: true})
      )
    );

    // Init values
    // WARNING: This needs to be here, before API calls, otherwise Safari has a bug rendering the form
    const initedFromLocalStorage : boolean = this.initFromLocalStorage();
    if (!initedFromLocalStorage) emitInfoMessage("Pass Prediction: Local storage contained older dashboard version data, ignoring.", this.infoMessageService);

    // Countries
    await this.getCountryCodes();

    // init clean values if local storage failed to be read
    if (!initedFromLocalStorage) this.initWithoutLocalStorage();

    // Set country list to static array (native select doesn't need filtering)
    this.countryList = of(this.countryCodes);

    // Make sure datepicker is disabled or enabled
    this.updateDatePickerState();
  }

  ngOnDestroy(){
    const formValue: any = this.passPredictionForm.getRawValue();
    // Convert FormGroup boolean values to string arrays
    formValue.serviceType = this.getSelectedServiceTypes();
    formValue.hardwareCapability = this.getSelectedHardwareCapabilities();
    localStorage.setItem(LACUNA_LOCAL_STORAGE_PP_NAME, JSON.stringify(formValue));
  }

  /**
   * Returns boolean that shows if local storage was used
   */
  initFromLocalStorage() : boolean {
    // If cookie is found, patch those values (but reset the date input, to avoid errors)
    let localData = localStorage.getItem(LACUNA_LOCAL_STORAGE_PP_NAME)
    // check if local storage exists
    if (!localData) {
      return false
    }
    // check if dashboard version matches local storage version
    let localDataAsObj : any = JSON.parse(localData);
    console.log(localDataAsObj)
    if (localDataAsObj['dashboardVersion'] != environment.version){
      return false
    }
    // countryCode is already a string, no conversion needed
    localDataAsObj['location']['latitude'] = localDataAsObj['location']['latitude'] == null? null : parseFloat(localDataAsObj['location']['latitude'])
    localDataAsObj['location']['longitude'] = localDataAsObj['location']['longitude'] == null? null : parseFloat(localDataAsObj['location']['longitude'])
    if (localDataAsObj['almanacVersion'] == null) localDataAsObj['almanacVersion'] = this.almanacVersions[0]

    // Convert serviceType string array to FormGroup booleans
    if (localDataAsObj['serviceType']) {
      localDataAsObj['serviceType'].forEach((id: string) => {
        const control = (this.passPredictionForm.controls.serviceType as FormGroup).get(id);
        if (control) control.setValue(true);
      });
      delete localDataAsObj['serviceType']; // Don't patch this one
    }
    // Same for hardwareCapability
    if (localDataAsObj['hardwareCapability']) {
      localDataAsObj['hardwareCapability'].forEach((id: string) => {
        const control = (this.passPredictionForm.controls.hardwareCapability as FormGroup).get(id);
        if (control) control.setValue(true);
      });
      delete localDataAsObj['hardwareCapability'];
    }

    // Patch values
    this.passPredictionForm.patchValue(localDataAsObj);
    return true
  }

  initWithoutLocalStorage() : void {
    // DateOption
    this.passPredictionForm.controls.dateOption.setValue(this.dateOptions[0])
    // Almanacs
    this.passPredictionForm.controls.almanacVersion.setValue(this.almanacVersions[0])
    // Services - select all by default
    this.serviceTypes.forEach(service => {
      const control = (this.passPredictionForm.controls.serviceType as FormGroup).get(service.id);
      if (control) control.setValue(true);
    });
    // Hardware Capabilities - select all by default
    this.hardwareCapabilities.forEach(hw => {
      const control = (this.passPredictionForm.controls.hardwareCapability as FormGroup).get(hw.id);
      if (control) control.setValue(true);
    });
  }

  updateDatePickerState(){
    if (this.passPredictionForm.controls.dateOption.value == this.dateOptions[0]){
      this.passPredictionForm.controls.date.disable();
      this.passPredictionForm.controls.date.reset();
    } else {
      this.passPredictionForm.controls.date.enable();
    }
  }

  async getCountryCodes(){
    await this.gatewayApiService.getPassPredictionCountriesOptions()
    .then((data) => {
      if (data.body) this.countryCodes = data.body
    })
  }

  async getUserLocation(){
    if (navigator.geolocation){
      navigator.geolocation.getCurrentPosition((position) => {
        this.passPredictionForm.controls.location.controls.latitude.setValue(parseFloat(position.coords.latitude.toFixed(6)));
        this.passPredictionForm.controls.location.controls.longitude.setValue(parseFloat(position.coords.longitude.toFixed(6)));
      }, (error) => {
        if (error.code == error.PERMISSION_DENIED) emitInfoMessage("Could not get your location: Permission Denied", this.infoMessageService)
        else if (error.code == error.POSITION_UNAVAILABLE) emitInfoMessage("Could not get your location: Position Unavailable", this.infoMessageService)
        else if (error.code == error.TIMEOUT) emitInfoMessage("Could not get your location: Timed Out", this.infoMessageService)
        else emitInfoMessage("Could not get your location: unknown error", this.infoMessageService);
        console.log("error message:",error.message);
      },{maximumAge:20000, timeout:10000});
    } else {
      emitInfoMessage("Could not get your location: Service not available in browser.", this.infoMessageService)
    };
  }

  async submit(){
    this.passPredictionForm.markAllAsTouched();
    // Date
    let dateFrom: Date;
    if (this.passPredictionForm.controls.dateOption.value == this.dateOptions[0]){
      // If Today
      dateFrom = this.todayDate;
    } else {
      // Check given custom date
      console.log(this.passPredictionForm.controls.date.value)
      if (this.passPredictionForm.controls.date.value == null){
        emitInfoMessage("Pass prediction: Need to supply a date",this.infoMessageService)
        return
      }
      // if (!this.passPredictionForm.controls.date.valid){
      //   emitInfoMessage("Pass prediction: Need to supply a valid date",this.infoMessageService)
      //   return
      // }
      // Use given date
      dateFrom = new Date(this.passPredictionForm.controls.date.value);
    }
    // Almanac
    if (this.passPredictionForm.controls.almanacVersion.value == null){
      emitInfoMessage("Pass prediction: Need to select an almanac",this.infoMessageService)
      return
    }
    // Country
    if (this.passPredictionForm.controls.countryCode.value == null){
      emitInfoMessage("Pass prediction: Need to select a country",this.infoMessageService)
      return
    }
    if (this.passPredictionForm.controls.countryCode.invalid){
      emitInfoMessage("Pass prediction: Need to supply a valid country from the list",this.infoMessageService)
      return
    }
    // Service
    if (this.getSelectedServiceTypes().length == 0){
      emitInfoMessage("Pass prediction: Need to select at least one service type",this.infoMessageService)
      return
    }
    // Hardware caps
    if (this.getSelectedHardwareCapabilities().length == 0){
      emitInfoMessage("Pass prediction: Need to select at least one hardware capability",this.infoMessageService)
      return
    }
    // Location
    if (this.passPredictionForm.controls.location.controls.latitude.value == null){
      emitInfoMessage("Pass prediction: Need to supply a latitude",this.infoMessageService)
      return
    }
    if (this.passPredictionForm.controls.location.controls.longitude.value == null){
      emitInfoMessage("Pass prediction: Need to supply a longitude",this.infoMessageService)
      return
    }
    if (this.passPredictionForm.controls.location.invalid){
      emitInfoMessage("Pass prediction: Need to supply a valid location",this.infoMessageService)
      return
    }
    let longitudeValue = this.passPredictionForm.controls.location.controls.longitude.value
    if (this.passPredictionForm.controls.location.controls.longitude.value == 180){
      longitudeValue = -180
    }
    console.log("Date:", dateFrom)
    // API call
    await this.gatewayApiService.getAlmanacPasses(
      // required
      this.passPredictionForm.controls.almanacVersion.value,
      this.passPredictionForm.controls.countryCode.value,
      this.passPredictionForm.controls.location.controls.latitude.value,
      longitudeValue,
      dateFrom,
      // optional
      this.getSelectedServiceTypes(),
      this.getSelectedHardwareCapabilities())
    .then((data) => {
      if (data.body){
        this.passDataFromApi = data.body
        this.passDataFromApi.forEach((pass : AlmanacPass) => {
            pass.contact_start = pass.contact_start.replace('T',' ').replace('Z','');
            pass.contact_tca = pass.contact_tca.replace('T',' ').replace('Z','');
            pass.contact_end = pass.contact_end.replace('T',' ').replace('Z','');
          });
        if (this.passDataFromApi.length == 0){
          emitInfoMessage("No passes found", this.infoMessageService)
        }
      };
      this.createPassDataWithGroupHeaders();
      // Save to localStorage with converted values
      const formValue: any = this.passPredictionForm.getRawValue();
      formValue.serviceType = this.getSelectedServiceTypes();
      formValue.hardwareCapability = this.getSelectedHardwareCapabilities();
      localStorage.setItem(LACUNA_LOCAL_STORAGE_PP_NAME, JSON.stringify(formValue));
    })
    .catch((error) => {
      emitInfoMessage("Error:" + error.status + " " + error.statusText, this.infoMessageService)
    })
  }

  getServiceFromItsId(service_id : string){
    let returnString: string = service_id;
    SERVICE_TYPES.forEach((serviceType) => {
      if (serviceType.id == service_id){
        returnString = serviceType.name
      }
    })
    return returnString
  }

  isInGroup(index : number, item : any): boolean {
    return item.groupName;
  }

  isGroup(item : AlmanacPass | GroupDayName): item is GroupDayName {
    return 'groupName' in item;
  }

  readonly browserUtcOffset: string = new Date().toLocaleString('en', {timeZoneName: 'shortOffset'}).split('GMT')[1] || '+0';

  toDisplayTime(utcDatetime: string): string {
    if (!this.passPredictionForm.controls.useLocalTime.value) return utcDatetime;
    return new Date(utcDatetime.replace(' ', 'T') + 'Z').toLocaleString('sv-SE');
  }

  extractDate(datetime: string): string {
    return this.toDisplayTime(datetime).split(' ')[0];
  }

  extractTime(datetime: string): string {
    return this.toDisplayTime(datetime).split(' ')[1];
  }

  createPassDataWithGroupHeaders(){
    // Reset this.passDataWithGroupHeaders
    this.passDataWithGroupHeaders = [];
    for (let index = 0; index < this.passDataFromApi.length; index++) {
      let thisPass : AlmanacPass = this.passDataFromApi[index];
      let nextPass : AlmanacPass = this.passDataFromApi[index+1];
      // If this pass is first in table, add group header and this pass
      if (index == 0){
        let group : GroupDayName = {'groupName' : this.extractDate(thisPass.contact_tca)}
        this.passDataWithGroupHeaders.push(group)
      }
      // Add this pass in any case
      this.passDataWithGroupHeaders.push(thisPass);
      // If next pass exist, check if it is a new group, if so add new group header
      if (nextPass != undefined){
        if (this.extractDate(thisPass.contact_tca) != this.extractDate(nextPass.contact_tca)){
          let group : GroupDayName = {'groupName' : this.extractDate(nextPass.contact_tca)}
          this.passDataWithGroupHeaders.push(group)
        }
      }
    }
  }

  getSelectedServiceTypes(): string[] {
    return this.serviceTypes
      .filter(service => (this.passPredictionForm.controls.serviceType as FormGroup).get(service.id)?.value)
      .map(st => st.id);
  }

  getSelectedHardwareCapabilities(): string[] {
    return this.hardwareCapabilities
      .filter(hw => (this.passPredictionForm.controls.hardwareCapability as FormGroup).get(hw.id)?.value)
      .map(hc => hc.id);
  }

  objToArray(obj : any){
    let optionsChosen : string[] = [];
    let key : string;
    for (key in obj) {
      if (obj.hasOwnProperty(key)) {
          if ((obj as any)[key]) optionsChosen.push(key);
      }
    }
  }

  protected readonly Object = Object;
}
